import { Link } from "react-router-dom";
import { ArrowRight, Shield, Zap, GitBranch, Server, Clock, Lock } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-zinc-950">
      {/* Nav */}
      <header className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-zinc-950 rounded-md flex items-center justify-center">
              <Zap className="w-4 h-4 text-[#FF4F00]" strokeWidth={2.5} />
            </div>
            <span className="font-extrabold text-lg tracking-tight">MigrateFlow</span>
          </div>
          <nav className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-login"
              className="text-sm font-semibold px-4 py-2 rounded-md text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 transition-all"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              data-testid="nav-register"
              className="text-sm font-semibold px-4 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all"
            >
              Get started
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative tech-grid">
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-zinc-300 rounded-full bg-white text-xs font-mono uppercase tracking-widest text-zinc-600 mb-8">
              <span className="w-1.5 h-1.5 bg-[#FF4F00] rounded-full animate-pulse" />
              IMAP · Real transfer engine · v1.0
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]">
              Move your mailbox.
              <br />
              <span className="text-[#FF4F00]">Not your mind.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-600 max-w-2xl leading-relaxed">
              MigrateFlow transfers every message, folder and label between Gmail,
              Outlook, Office 365 and any IMAP server — with zero downtime and full
              audit logs.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#CC3F00] text-white font-semibold px-6 py-3 rounded-md transition-all active:scale-95"
              >
                Start a migration
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-zinc-300 bg-white text-zinc-900 font-semibold px-6 py-3 rounded-md hover:bg-zinc-50 transition-all active:scale-95"
              >
                I have an account
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
              {[
                ["10M+", "Messages moved"],
                ["99.98%", "Delivery success"],
                ["4", "Providers supported"],
                ["0", "Message loss"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-extrabold text-3xl">{n}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Providers strip */}
      <section className="border-y border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">
            Works with every mailbox you already use
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {["Gmail", "Outlook / Office 365", "Yahoo Mail", "Any IMAP server"].map((p) => (
              <div
                key={p}
                className="bg-white border border-zinc-200 rounded-md px-5 py-4 font-semibold flex items-center gap-3"
              >
                <Server className="w-4 h-4 text-[#FF4F00]" />
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
            Built for engineers, trusted by admins
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Everything you need to move a mailbox — and nothing you don't.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 border border-zinc-200 rounded-md p-8 bg-white">
            <GitBranch className="w-6 h-6 text-[#FF4F00] mb-4" />
            <h3 className="text-2xl font-bold mb-2">Folder-perfect replication</h3>
            <p className="text-zinc-600">
              Nested folders, custom labels, message flags and internal dates
              are preserved 1-for-1 across providers.
            </p>
          </div>
          <div className="border border-zinc-200 rounded-md p-8 bg-white">
            <Shield className="w-6 h-6 text-[#FF4F00] mb-4" />
            <h3 className="text-2xl font-bold mb-2">Zero-store credentials</h3>
            <p className="text-zinc-600">
              App-password based auth. Credentials live only for the duration of the job.
            </p>
          </div>
          <div className="border border-zinc-200 rounded-md p-8 bg-white">
            <Clock className="w-6 h-6 text-[#FF4F00] mb-4" />
            <h3 className="text-2xl font-bold mb-2">Live progress</h3>
            <p className="text-zinc-600">
              Watch every folder tick over in real time with a streaming log terminal.
            </p>
          </div>
          <div className="md:col-span-2 border border-zinc-200 rounded-md p-8 bg-white">
            <Lock className="w-6 h-6 text-[#FF4F00] mb-4" />
            <h3 className="text-2xl font-bold mb-2">Idempotent, resumable, auditable</h3>
            <p className="text-zinc-600">
              Full audit logs per message, resumable transfers, and safe cancel at any time.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Ready to move?
          </h2>
          <p className="mt-4 text-zinc-400 text-lg max-w-xl mx-auto">
            Set up your first migration in under 60 seconds. No credit card required.
          </p>
          <Link
            to="/register"
            data-testid="footer-cta"
            className="mt-10 inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#CC3F00] text-white font-semibold px-8 py-3.5 rounded-md transition-all active:scale-95"
          >
            Create your account
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs font-mono text-zinc-500">
          <span>© 2026 MigrateFlow</span>
          <span>Built for high-volume email operations.</span>
        </div>
      </footer>
    </div>
  );
}
